// ICS calendar generator. Vegas (America/Los_Angeles) timezone with full
// VTIMEZONE block + DST rules. RFC 5545 escaping + 75-octet line folding.

import type { FestSet } from "../data/schedule";

const TZID = "America/Los_Angeles";

export type ICSOpts = {
  walkBufferMin: number;     // standard alarm offset, default 15
  finalReminder: boolean;    // 1-min "now" alarm, default true
  closerLeadMin: number;     // extra alarm for closers, default 30
  fireworksLeadMin: number;  // alarm before each fireworks event, default 5
  includeFireworks: boolean; // default true
  calendarName: string;      // default "EDC Las Vegas 2026 — My Sets"
};

export const DEFAULT_ICS_OPTS: ICSOpts = {
  walkBufferMin: 15,
  finalReminder: true,
  closerLeadMin: 30,
  fireworksLeadMin: 5,
  includeFireworks: true,
  calendarName: "EDC Las Vegas 2026 — My Sets",
};

export function buildICS(faves: FestSet[], opts: Partial<ICSOpts> = {}): string {
  const o = { ...DEFAULT_ICS_OPTS, ...opts };
  const lines: string[] = [
    "BEGIN:VCALENDAR",
    "VERSION:2.0",
    "PRODID:-//bfer.land//EDC 2026 Wallpaper Tool//EN",
    "CALSCALE:GREGORIAN",
    "METHOD:PUBLISH",
    `X-WR-CALNAME:${escapeICS(o.calendarName)}`,
    `X-WR-TIMEZONE:${TZID}`,
    "X-WR-CALDESC:Personal favorites with walk-buffer alarms and fireworks markers.",
    ...timezoneBlock(),
  ];

  for (const f of faves) {
    lines.push(...eventBlock(f, o));
  }

  if (o.includeFireworks) {
    for (const day of [1, 2, 3] as const) {
      lines.push(...fireworksBlock(day, o));
    }
  }

  lines.push("END:VCALENDAR");
  return lines.map(foldLine).join("\r\n") + "\r\n";
}

export function downloadICS(content: string, filename: string): void {
  const blob = new Blob([content], { type: "text/calendar;charset=utf-8" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  setTimeout(() => URL.revokeObjectURL(url), 1000);
}

function timezoneBlock(): string[] {
  return [
    "BEGIN:VTIMEZONE",
    `TZID:${TZID}`,
    "X-LIC-LOCATION:America/Los_Angeles",
    "BEGIN:DAYLIGHT",
    "DTSTART:20070311T020000",
    "RRULE:FREQ=YEARLY;BYMONTH=3;BYDAY=2SU",
    "TZOFFSETFROM:-0800",
    "TZOFFSETTO:-0700",
    "TZNAME:PDT",
    "END:DAYLIGHT",
    "BEGIN:STANDARD",
    "DTSTART:20071104T020000",
    "RRULE:FREQ=YEARLY;BYMONTH=11;BYDAY=1SU",
    "TZOFFSETFROM:-0700",
    "TZOFFSETTO:-0800",
    "TZNAME:PST",
    "END:STANDARD",
    "END:VTIMEZONE",
  ];
}

function eventBlock(f: FestSet, o: ICSOpts): string[] {
  const summary = `${f.artistDisplay} @ ${f.stage}`;
  const description = [
    "EDC Las Vegas 2026 — 30th Anniversary",
    f.artist,
    `Stage: ${f.stage}`,
    `Set: ${fmtTime(f.startUTC)} – ${fmtTime(f.endUTC)} PT`,
    f.isCloser ? "⭐ CLOSER — last set of the night on this stage" : "",
  ].filter(Boolean).join("\\n");

  const out: string[] = [
    "BEGIN:VEVENT",
    `UID:${f.id}@edc2026.bfer.land`,
    `DTSTAMP:${nowUtcStamp()}`,
    `DTSTART;TZID=${TZID}:${fmtLocal(f.startUTC)}`,
    `DTEND;TZID=${TZID}:${fmtLocal(f.endUTC)}`,
    `SUMMARY:${escapeICS(summary)}`,
    `LOCATION:${escapeICS(f.stage + ", Las Vegas Motor Speedway")}`,
    `DESCRIPTION:${description}`,
    "CATEGORIES:Music,Festival,EDC",
  ];

  if (f.isCloser) {
    out.push(...alarm(`-PT${o.closerLeadMin}M`, `${summary} starts in ${o.closerLeadMin} min — closer set, head over!`));
  }
  out.push(...alarm(`-PT${o.walkBufferMin}M`, `${summary} in ${o.walkBufferMin} min — start walking`));
  if (o.finalReminder) {
    out.push(...alarm("-PT1M", `${summary} starting now`));
  }
  out.push("END:VEVENT");
  return out;
}

function fireworksBlock(day: 1 | 2 | 3, o: ICSOpts): string[] {
  // Fireworks 1:42-1:47 AM PT. Day 1 (Friday night) → fireworks on May 16, etc.
  const dayOfMonth = 15 + day; // Day 1 → May 16, Day 2 → May 17, Day 3 → May 18
  const dateStr = `2026051${dayOfMonth - 10}`; // 16/17/18 → "20260516" etc.
  return [
    "BEGIN:VEVENT",
    `UID:fireworks-day${day}-2026@edc2026.bfer.land`,
    `DTSTAMP:${nowUtcStamp()}`,
    `DTSTART;TZID=${TZID}:${dateStr}T014200`,
    `DTEND;TZID=${TZID}:${dateStr}T014700`,
    "SUMMARY:★ EDC Fireworks ★",
    "LOCATION:Kinetic Field area, Las Vegas Motor Speedway",
    "DESCRIPTION:Fireworks at EDC Las Vegas 2026.\\nKinetic Field pauses 1:42-1:47 AM. Best viewed from KIN, Cosmic Meadow, or anywhere with a sightline.",
    "CATEGORIES:Fireworks,Festival,EDC",
    ...alarm(`-PT${o.fireworksLeadMin}M`, "Fireworks in 5 minutes — find a good sightline"),
    "END:VEVENT",
  ];
}

function alarm(trigger: string, desc: string): string[] {
  return [
    "BEGIN:VALARM",
    "ACTION:DISPLAY",
    `TRIGGER:${trigger}`,
    `DESCRIPTION:${escapeICS(desc)}`,
    "END:VALARM",
  ];
}

function fmtLocal(d: Date): string {
  // Output Vegas-local clock time (PDT = UTC-7) in compact ICS form.
  const utc = d.getTime();
  const pdt = new Date(utc - 7 * 60 * 60 * 1000);
  const Y = pdt.getUTCFullYear();
  const M = (pdt.getUTCMonth() + 1).toString().padStart(2, "0");
  const D = pdt.getUTCDate().toString().padStart(2, "0");
  const h = pdt.getUTCHours().toString().padStart(2, "0");
  const m = pdt.getUTCMinutes().toString().padStart(2, "0");
  return `${Y}${M}${D}T${h}${m}00`;
}

function fmtTime(d: Date): string {
  const utc = d.getTime();
  const pdt = new Date(utc - 7 * 60 * 60 * 1000);
  const h = pdt.getUTCHours().toString().padStart(2, "0");
  const m = pdt.getUTCMinutes().toString().padStart(2, "0");
  return `${h}:${m}`;
}

function nowUtcStamp(): string {
  const d = new Date();
  const Y = d.getUTCFullYear();
  const M = (d.getUTCMonth() + 1).toString().padStart(2, "0");
  const D = d.getUTCDate().toString().padStart(2, "0");
  const h = d.getUTCHours().toString().padStart(2, "0");
  const m = d.getUTCMinutes().toString().padStart(2, "0");
  const s = d.getUTCSeconds().toString().padStart(2, "0");
  return `${Y}${M}${D}T${h}${m}${s}Z`;
}

function escapeICS(s: string): string {
  return s
    .replace(/\\/g, "\\\\")
    .replace(/,/g, "\\,")
    .replace(/;/g, "\\;")
    .replace(/\n/g, "\\n");
}

function foldLine(line: string): string {
  // RFC 5545 — fold lines longer than 75 octets with CRLF + space continuation.
  const enc = new TextEncoder();
  const bytes = enc.encode(line);
  if (bytes.length <= 75) return line;
  const chunks: string[] = [];
  let buf: number[] = [];
  for (let i = 0; i < bytes.length; i++) {
    buf.push(bytes[i]);
    const limit = chunks.length === 0 ? 75 : 74;
    if (buf.length >= limit) {
      chunks.push(new TextDecoder().decode(new Uint8Array(buf)));
      buf = [];
    }
  }
  if (buf.length) chunks.push(new TextDecoder().decode(new Uint8Array(buf)));
  return chunks.join("\r\n ");
}
